import React from "react"
import { useTranslation } from "react-i18next"
import { faBell, faClose, faGift, faGear, faMessage, faPercent, faTruckFast } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import Button, { ButtonIcon } from "../../../../components/Button/Button"
import InputField from "../../../../components/InputField/InputField"
import { SimpleToggle } from "../../../../components/SimpleToggle/SimpleToggle"
import type { IWhatsAppAiMessageSettings } from "../../../../Objects/WhatsAppAutomation/WhatsAppAiMessageSettings"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"

type TemplateKey = "orderStatusTemplate" | "remarketingTemplate" | "awardTemplate" | "promotionTemplate"

type AutomationMessageType = "orderStatus" | "remarketing" | "award" | "promotion"

type Props = {
  isOpen: boolean
  settings: IWhatsAppAiMessageSettings
  onClose: () => void
  onChange: (settings: IWhatsAppAiMessageSettings) => void
  onSave: () => void
}

const variables = [
  "{{customerName}}",
  "{{storeName}}",
  "{{orderId}}",
  "{{orderStatus}}",
  "{{orderTotal}}",
  "{{deliveryAddress}}",
  "{{publicMenuLink}}",
  "{{awardName}}",
  "{{awardLink}}",
  "{{promotionName}}",
  "{{promotionLink}}",
]

const messageTypeAnimationDelay: Record<AutomationMessageType, string> = {
  orderStatus: "0.05s",
  award: "0.12s",
  promotion: "0.19s",
  remarketing: "0.26s",
}

export const WhatsAppMessageSettingsPopup: React.FC<Props> = ({ isOpen, settings, onClose, onChange, onSave }) => {
  const { t } = useTranslation(["pages", "buttons", "helpPopup"])

  if (!isOpen) {
    return null
  }

  const patch = (next: Partial<IWhatsAppAiMessageSettings>) => onChange({ ...settings, ...next })

  const updateTemplate = (key: TemplateKey, value: string) => patch({ [key]: value })

  const buildHelpProps = (type: AutomationMessageType) => ({
    title: t(`helpPopup:sections.whatsAppMessageSettings.${type}.title`),
    description: t(`helpPopup:sections.whatsAppMessageSettings.${type}.description`),
    steps: t(`helpPopup:sections.whatsAppMessageSettings.${type}.steps`, { returnObjects: true }) as string[],
    bullets: t(`helpPopup:sections.whatsAppMessageSettings.${type}.bullets`, { returnObjects: true }) as string[],
  })

  const renderMessageToggle = (
    type: AutomationMessageType,
    title: string,
    description: string,
    value: boolean,
    onToggle: (value: boolean) => void,
    icon: typeof faMessage,
  ) => {
    const helpProps = buildHelpProps(type)

    return (
      <Animate type={AnimationTypes.slideUpIn} duration="0.55s" delay={messageTypeAnimationDelay[type]}>
        <div
          className={`rounded-lg border-2 p-3 transition-all duration-300 ${value ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}
        >
          <div className="mb-3 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start">
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${value ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
            >
              <FontAwesomeIcon icon={icon} />
            </span>

            <div className="min-w-0 flex-1">
              <strong className="block text-sm text-slate-900">{title}</strong>

              <p className="mt-1 text-xs leading-relaxed text-slate-500">{description}</p>
            </div>
          </div>

          <div className="flex flex-row items-center justify-between">
            <div className="w-full mr-2">
              <SimpleToggle
                title={t("label_whatsapp_message_settings_enable_event")}
                value={value}
                onChange={onToggle}
                animationDuration="0.35s"
                descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppMessageSettingsPopup.toggle.whatsappMessageSettingsEnableEvent"
              />
            </div>

            <PopupHelpButton {...helpProps} />
          </div>
        </div>
      </Animate>
    )
  }

  const renderTemplate = (
    title: string,
    description: string,
    key: TemplateKey,
    enabled: boolean,
    type: AutomationMessageType,
    index: number,
  ) => (
    <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
      <section
        className={`rounded-lg border-2 p-3 transition-all duration-300 ${enabled ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}
      >
        <div className="flex flex-col">
          <div className="flex flex-row items-center justify-between">
            <strong className="block text-sm text-slate-900">{title}</strong>

            <PopupHelpButton {...buildHelpProps(type)} />
          </div>

          <p className="mt-1 text-xs leading-relaxed text-slate-500">{description}</p>
        </div>

        <textarea
          className="mt-3 w-full rounded-lg border-2 border-slate-200 bg-white p-2 text-xs text-slate-700 outline-none transition-all duration-300 focus:border-emerald-400"
          rows={3}
          maxLength={1000}
          value={settings[key]}
          onChange={(event) => updateTemplate(key, event.target.value)}
          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppMessageSettingsPopup.input.input"
        />
        <p className="mt-1 text-right text-[11px] text-slate-400">{settings[key].length}/1000</p>
      </section>
    </Animate>
  )

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              iconColor={THEME.colors.iconBGColor}
              title={t("label_whatsapp_message_settings_title")}
              icon={faGear}
            >
              <div className="flex items-center gap-2">
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.whatsapp-message-settings"
                  title={t("helpPopup:global.whatsAppMessageSettings.title")}
                  description={t("helpPopup:global.whatsAppMessageSettings.description")}
                  steps={t("helpPopup:global.whatsAppMessageSettings.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.whatsAppMessageSettings.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppMessageSettingsPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="max-h-[72vh] overflow-x-hidden overflow-y-auto p-2">
              <div className="grid min-w-0 gap-3">
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <section className="rounded-lg border-2 border-orange-100 bg-orange-50 p-3">
                    <strong className="block text-sm text-orange-900">
                      {t("label_whatsapp_message_settings_flow_title")}
                    </strong>

                    <p className="mt-1 text-xs font-medium leading-relaxed text-orange-800">
                      {t("label_whatsapp_message_settings_flow_description")}
                    </p>
                  </section>
                </Animate>

                <SimpleToggle
                  title={t("label_whatsapp_message_settings_enable")}
                  value={settings.isEnabled}
                  onChange={(value) => patch({ isEnabled: value })}
                  animationDuration="0.35s"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppMessageSettingsPopup.toggle.whatsappMessageSettingsEnable"
                />

                <section className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3">
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <div>
                      <strong className="block text-sm text-slate-900">
                        {t("label_whatsapp_message_settings_events_title")}
                      </strong>

                      <p className="mt-1 text-xs text-slate-500">
                        {t("label_whatsapp_message_settings_events_description")}
                      </p>
                    </div>
                  </Animate>

                  <div className="mt-3 grid gap-2 md:grid-cols-2">
                    {renderMessageToggle(
                      "orderStatus",
                      t("label_whatsapp_message_order_status"),
                      t("label_whatsapp_message_order_status_execution_description"),
                      settings.isOrderStatusEnabled,
                      (value) => patch({ isOrderStatusEnabled: value }),
                      faTruckFast,
                    )}

                    {renderMessageToggle(
                      "award",
                      t("label_whatsapp_message_award"),
                      t("label_whatsapp_message_award_execution_description"),
                      settings.isAwardEnabled,
                      (value) => patch({ isAwardEnabled: value }),
                      faGift,
                    )}

                    {renderMessageToggle(
                      "promotion",
                      t("label_whatsapp_message_promotion"),
                      t("label_whatsapp_message_promotion_execution_description"),
                      settings.isPromotionEnabled,
                      (value) => patch({ isPromotionEnabled: value }),
                      faPercent,
                    )}

                    {renderMessageToggle(
                      "remarketing",
                      t("label_whatsapp_message_remarketing"),
                      t("label_whatsapp_message_remarketing_execution_description"),
                      settings.isRemarketingEnabled,
                      (value) => patch({ isRemarketingEnabled: value }),
                      faBell,
                    )}
                  </div>
                </section>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <section className="rounded-lg border-2 border-slate-200 bg-white p-3">
                    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1">
                        <strong className="block text-sm text-slate-900">
                          {t("label_whatsapp_message_settings_remarketing_title")}
                        </strong>

                        <p className="mb-3 mt-1 text-xs text-slate-500">
                          {t("label_whatsapp_message_settings_remarketing_description")}
                        </p>
                      </div>

                      <div className="self-end sm:self-start">
                        <PopupHelpButton {...buildHelpProps("remarketing")} />
                      </div>
                    </div>

                    <InputField
                      type="number"
                      icon={faMessage}
                      title={t("label_whatsapp_message_remarketing_interval")}
                      placeholder={t("label_whatsapp_message_remarketing_interval")}
                      value={String(settings.remarketingIntervalDays)}
                      onChange={(event) => patch({ remarketingIntervalDays: Number(event.target.value || 15) })}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppMessageSettingsPopup.input.whatsappMessageRemarketingInterval"
                    />
                  </section>
                </Animate>

                <section className="rounded-lg border-2 border-slate-200 bg-white p-3">
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <div>
                      <strong className="block text-sm text-slate-900">
                        {t("label_whatsapp_message_settings_templates_title")}
                      </strong>

                      <p className="mt-1 text-xs text-slate-500">
                        {t("label_whatsapp_message_settings_templates_description")}
                      </p>
                    </div>
                  </Animate>

                  <div className="mt-3 grid gap-3">
                    {renderTemplate(
                      t("label_whatsapp_message_order_status_template"),
                      t("label_whatsapp_message_order_status_description"),
                      "orderStatusTemplate",
                      settings.isOrderStatusEnabled,
                      "orderStatus",
                      0,
                    )}

                    {renderTemplate(
                      t("label_whatsapp_message_remarketing_template"),
                      t("label_whatsapp_message_remarketing_description"),
                      "remarketingTemplate",
                      settings.isRemarketingEnabled,
                      "remarketing",
                      1,
                    )}

                    {renderTemplate(
                      t("label_whatsapp_message_award_template"),
                      t("label_whatsapp_message_award_description"),
                      "awardTemplate",
                      settings.isAwardEnabled,
                      "award",
                      2,
                    )}

                    {renderTemplate(
                      t("label_whatsapp_message_promotion_template"),
                      t("label_whatsapp_message_promotion_description"),
                      "promotionTemplate",
                      settings.isPromotionEnabled,
                      "promotion",
                      3,
                    )}
                  </div>
                </section>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <section className="rounded-lg border-2 border-indigo-100 bg-indigo-50 p-3">
                    <strong className="text-xs text-indigo-900">{t("label_whatsapp_message_variables_title")}</strong>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {variables.map((item, index) => (
                        <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s" key={item}>
                          <code className="rounded border border-indigo-200 bg-white px-2 py-1 text-[11px] text-indigo-800">
                            {item}
                          </code>
                        </Animate>
                      ))}
                    </div>
                  </section>
                </Animate>

                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="flex justify-end">
                    <Button
                      bgColor={THEME.colors.buttonBGSaveColor}
                      label={t("buttons:button_save")}
                      onClick={onSave}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppCrm.components.whatsAppMessageSettingsPopup.button.save"
                    />
                  </div>
                </Animate>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
